<template>
  <modal
    :show="isVisible"
    :title="t('Selecionar dispositivo')"
    :close-label="t('Cancelar')"
    elem-id="modal_devicePicker"
    size="small"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <p style="margin: 0 0 12px; font-size: 13px; color: var(--app-text-secondary-color);">
        {{ t('Este cliente possui múltiplos dispositivos WhatsApp disponíveis. Selecione um para abrir a conversa.') }}
      </p>
      <div class="device-list">
        <button
          v-for="device in candidates"
          :key="device.id"
          class="device-option"
          :data-testid="`messaging-inbox-device-picker-option-${device.id}`"
          @click="select(device.id)"
        >
          <i class="pi pi-whatsapp device-icon" />
          <span class="device-name">{{ device.instanceName }}</span>
          <span class="device-label">{{ device.label }}</span>
        </button>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';

const { t } = useI18n();

type DeviceId = number | string;

interface DeviceCandidate {
  id: DeviceId;
  instanceName: string;
  label?: string;
}

interface OpenPayload {
  candidates: DeviceCandidate[];
  onSelect: (deviceId: DeviceId) => void;
}

const isVisible = ref(false);
const candidates = ref<DeviceCandidate[]>([]);
let onSelectCallback: ((deviceId: DeviceId) => void) | null = null;

const handleOpen = (payload?: OpenPayload): void => {
  if (!payload) return;
  candidates.value = payload.candidates;
  onSelectCallback = payload.onSelect;
  isVisible.value = true;
};

const handleClose = (): void => {
  isVisible.value = false;
  candidates.value = [];
  onSelectCallback = null;
};

const select = (deviceId: DeviceId): void => {
  onSelectCallback?.(deviceId);
  handleClose();
};

onMounted(() => {
  eventBus.on('modal.whatsapp-device-picker.open', handleOpen);
  eventBus.on('modal.whatsapp-device-picker.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.whatsapp-device-picker.open', handleOpen);
  eventBus.off('modal.whatsapp-device-picker.close', handleClose);
});
</script>

<style scoped>
.device-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.device-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--app-bg-light);
  border: 1px solid var(--app-border-color);
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
  width: 100%;
}

.device-option:hover {
  background: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.08);
  border-color: var(--app-primary-color);
}

.device-icon {
  color: var(--app-success-color);
  font-size: 16px;
  flex-shrink: 0;
}

.device-name {
  font-weight: 600;
  font-size: 13px;
  color: var(--app-text-color);
}

.device-label {
  font-size: 11px;
  color: var(--app-text-secondary-color);
  margin-left: auto;
}
</style>
